// client/src/pages/admin/ManageFieldDefinitions.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';

// ——— Field Definition Modal ———————————————————————————
function FieldModal({ field, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    fieldtype: field?.fieldtype || 'message',
    fieldkey: field?.fieldkey || '',
    defaultlabel: field?.defaultlabel || '',
    defaultplaceholder: field?.defaultplaceholder || '',
    inputtype: field?.inputtype || 'text',
    displayorder: field?.displayorder || 0,
    isactive: field?.isactive !== false,
  });
  const [error, setError] = useState('');

  const isEditing = !!field?.fielddefinitionid;

  // Auto-generate key from label for new fields
  const handleLabelChange = (label) => {
    setForm((prev) => ({
      ...prev,
      defaultlabel: label,
      // Only auto-generate key if it hasn't been manually edited
      fieldkey: isEditing ? prev.fieldkey : label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
    }));
  };

  const handleSave = () => {
    setError('');
    if (!form.fieldtype) {
      setError('Field type is required');
      return;
    }
    if (!form.fieldkey.trim()) {
      setError('Field key is required');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(form.fieldkey)) {
      setError('Field key must start with a letter and contain only lowercase letters, numbers, and underscores');
      return;
    }
    if (!form.defaultlabel.trim()) {
      setError('Default label is required');
      return;
    }
    onSave({
      ...form,
      displayorder: parseInt(form.displayorder) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">
            {isEditing ? 'Edit Field Definition' : 'Add Field Definition'}
          </h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
          {error && <p className="text-red-400 text-base">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Field Type *</label>
              <select
                value={form.fieldtype}
                onChange={(e) => setForm({ ...form, fieldtype: e.target.value })}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
              >
                <option value="name">Name</option>
                <option value="message">Message</option>
              </select>
              <p className="text-royal-200 text-xs mt-1">Categorizes the field</p>
            </div>

            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Input Type *</label>
              <select
                value={form.inputtype}
                onChange={(e) => setForm({ ...form, inputtype: e.target.value })}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
              >
                <option value="text">Text (single line)</option>
                <option value="textarea">Textarea (multi-line)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Default Label *</label>
            <input
              type="text"
              value={form.defaultlabel}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="e.g., Recipient Name"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
            <p className="text-royal-200 text-xs mt-1">Shown in the form (can be overridden per template)</p>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Field Key *</label>
            <input
              type="text"
              value={form.fieldkey}
              onChange={(e) => setForm({ ...form, fieldkey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              placeholder="e.g., recipient_name"
              disabled={isEditing}
              className={`w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors font-mono ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            <p className="text-royal-200 text-xs mt-1">
              Used in HTML templates as <code className="text-gold">data.{form.fieldkey || 'field_key'}</code>
              {isEditing && ' (cannot be changed)'}
            </p>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Default Placeholder</label>
            <textarea
              value={form.defaultplaceholder}
              onChange={(e) => setForm({ ...form, defaultplaceholder: e.target.value })}
              placeholder="e.g., Enter their name..."
              rows={form.inputtype === 'textarea' ? 4 : 2}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors resize-y"
            />
            {form.inputtype === 'textarea' && (
              <p className="text-royal-200 text-xs mt-1">Press Enter for multiple lines (useful for lists/suggestions)</p>
            )}
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Display Order</label>
            <input
              type="number"
              value={form.displayorder}
              onChange={(e) => setForm({ ...form, displayorder: e.target.value })}
              min={0}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            />
            <p className="text-royal-200 text-xs mt-1">Controls sort order in dropdowns</p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isactive"
              checked={form.isactive}
              onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
              className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
            />
            <label htmlFor="isactive" className="text-royal-200 text-base">Active (available for use)</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 pt-4 border-t border-royal-700/50">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-200 text-base hover:text-white transition-colors">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Field'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ——— Main Component ———————————————————————————————————
export default function ManageFieldDefinitions() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchFields = async () => {
    setLoading(true);
    try {
      const data = await api.get('/admin/template-field-definitions');
      setFields(data.fieldDefinitions || []);
    } catch (err) {
      console.error('Fetch fields error:', err);
      setError('Failed to load field definitions');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      if (editingField?.fielddefinitionid) {
        await api.put(`/admin/template-field-definitions/${editingField.fielddefinitionid}`, form);
      } else {
        await api.post('/admin/template-field-definitions', form);
      }
      setEditingField(null);
      fetchFields();
    } catch (err) {
      setError(err.data?.error || 'Failed to save field definition');
    }
    setSaving(false);
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`Delete "${field.defaultlabel}"? This cannot be undone.`)) return;

    setDeleting(field.fielddefinitionid);
    setError('');
    try {
      await api.del(`/admin/template-field-definitions/${field.fielddefinitionid}`);
      fetchFields();
    } catch (err) {
      setError(err.data?.error || 'Failed to delete field definition');
    }
    setDeleting(null);
  };

  // Group fields by type
  const nameFields = fields.filter((f) => f.fieldtype === 'name');
  const messageFields = fields.filter((f) => f.fieldtype === 'message');

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-gold">Field Definitions</h1>
          <p className="text-royal-200 text-base">Reusable field types for templates</p>
        </div>
        <Button onClick={() => setEditingField({})}>+ Add Field</Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-base">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      ) : fields.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-royal-200 text-base mb-2">No field definitions yet</p>
          <button
            onClick={() => setEditingField({})}
            className="text-gold text-base hover:text-gold-light transition-colors"
          >
            Add your first field →
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Name Fields */}
          <div>
            <h2 className="text-lg font-medium text-blue-300 mb-3 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              Name Fields ({nameFields.length})
            </h2>
            {nameFields.length === 0 ? (
              <p className="text-royal-200 text-sm pl-5">No name fields defined</p>
            ) : (
              <div className="space-y-2">
                {nameFields.map((field) => (
                  <FieldRow
                    key={field.fielddefinitionid}
                    field={field}
                    onEdit={() => setEditingField(field)}
                    onDelete={() => handleDelete(field)}
                    deleting={deleting === field.fielddefinitionid}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Message Fields */}
          <div>
            <h2 className="text-lg font-medium text-purple-300 mb-3 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500"></span>
              Message Fields ({messageFields.length})
            </h2>
            {messageFields.length === 0 ? (
              <p className="text-royal-200 text-sm pl-5">No message fields defined</p>
            ) : (
              <div className="space-y-2">
                {messageFields.map((field) => (
                  <FieldRow
                    key={field.fielddefinitionid}
                    field={field}
                    onEdit={() => setEditingField(field)}
                    onDelete={() => handleDelete(field)}
                    deleting={deleting === field.fielddefinitionid}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit/Add Modal */}
      {editingField !== null && (
        <FieldModal
          field={editingField}
          onSave={handleSave}
          onClose={() => setEditingField(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ——— Field Row Component ——————————————————————————————
function FieldRow({ field, onEdit, onDelete, deleting }) {
  return (
    <div
      className={`bg-royal-800/40 border rounded-xl p-4 flex items-center gap-4 ${
        field.isactive ? 'border-royal-700/50' : 'border-red-600/30 opacity-60'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <code className="text-gold text-sm font-mono">{field.fieldkey}</code>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            field.inputtype === 'textarea' 
              ? 'bg-royal-700 text-royal-200' 
              : 'bg-royal-700/50 text-royal-200'
          }`}>
            {field.inputtype}
          </span>
          {!field.isactive && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/50 text-red-400">
              inactive
            </span>
          )}
        </div>
        <p className="text-white text-base">{field.defaultlabel}</p>
        {field.defaultplaceholder && (
          <p className="text-royal-200 text-sm truncate">"{field.defaultplaceholder}"</p>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 text-royal-200 hover:text-gold rounded-lg hover:bg-royal-700/50 transition-colors"
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          className="p-2 text-royal-200 hover:text-red-400 rounded-lg hover:bg-royal-700/50 transition-colors disabled:opacity-50"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}
